import Button from '@components/Button';
import Column from '@components/Column';
import Divider from '@components/Divider';
import Row from '@components/Row';
import Text from '@components/Text';
import Tooltip from '@components/Tooltip';
import colors from '@constants/colors';
import styled from '@emotion/styled';
import useAppCustom from '@hooks/useAppCustom';
import ArrowLineRight from '@icons/arrow/ArrowLineRight';
import { OptionGroup } from '@models/item';
import { useFlow } from '@stackflow/react';
import { useItemActivityId } from '@store/item/context/ItemActivityContext';
import useOptionControl from '@store/item/hooks/useOptionControl';
import { pressActionEffect } from '@styles/mixin';
import { Fragment, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import SelectedPersonalOptionList from './SelectedPersonalOptionList';
import useSelectedPersonalOptions from './useSelectedPersonalOptions';

interface Props {
    itemId: string;
    personalOptionGroups: OptionGroup[];
}

/**
 * 퍼스널 옵션 열기 버튼 및 선택된 옵션 목록
 * @param itemId
 * @param personalOptionGroups
 * @constructor
 */
function PersonalOptionButton({ itemId, personalOptionGroups }:Props) {
    const { push } = useFlow();
    const itemActivityId = useItemActivityId();
    const { t } = useTranslation('item');
    const { optionCollapsed } = useAppCustom().item;
    const { resetPersonalOptions } = useOptionControl({ itemId });
    const hasSelected = useSelectedPersonalOptions().length > 0;
    // 헤더 전체가 퍼스널 옵션 화면으로 가는 버튼이라 초기화 클릭은 위로 올리지 않는다.
    const handleReset = (e: MouseEvent) => {
        e.stopPropagation();
        resetPersonalOptions();
    };
    if (personalOptionGroups.length > 0 && optionCollapsed) {
        return (
            <Fragment>
                <Divider/>
                <Container spacing={4} fullWidth>
                    <ButtonRow
                        id='personal-option-button'
                        onClick={() => { push('PersonalOptionActivity', { itemId, itemActivityId }); }}
                        fullWidth
                        justify='space-between'>
                        <Row spacing={3}>
                            <Text size='title' fontWeight='bold'>{t('option.personal')}</Text>
                            {hasSelected && (
                                <Button variant='outlined'
                                    id='reset-option-button'
                                    size='xsmall'
                                    onClick={handleReset}>
                                    {t('option.resetAll')}
                                </Button>
                            )}
                        </Row>
                        <ArrowLineRight size={16} color={colors.iconDefault}/>
                        <Tooltip visibleKey='personalOption' top={-16} align='right' right={4}>
                            {t('option.personalTooltip')}
                        </Tooltip>
                    </ButtonRow>
                    <SelectedPersonalOptionList itemId={itemId} />
                </Container>
            </Fragment>
        );
    }
    return null;
}

const Container = styled(Column)`
    padding: var(--spacing-4);
`;

const ButtonRow = styled(Row)`
    cursor: pointer;
    position: relative;
    ${pressActionEffect}
`;

export default PersonalOptionButton;
